import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import { cubingApi } from "~/redux/warehouse/cubing";
import {
  GetSuggestedCompartmentAndMaxFillResponse,
  PutAwayTaskSummaryDto
} from "~/types/api";

import { SearchData } from "./useGetPutawayTasks";

const emptyArray: number[] = [];

export type PreviousPutawayTask = PutAwayTaskSummaryDto & {
  binId: number;
  portId: number;
  compartment: number;
};

type AutostorePutawayState = {
  selectedRowId: Guid | null;
  selectedCompartment: number | undefined;
  newSelectedCompartment: number | null;
  changedQuantity: number | undefined;
  inventoryDate: Date | string | null;
  lotNumber: string;
  isLotNumberFieldFocused: boolean;
  searchData: SearchData;
  isPutawayTaskTableRefreshed: boolean;
  portPollingActive: boolean;
  closePortBtnDisabled: boolean;
  onLeaveInputTextShown: boolean;
  isConfirmInductionButtonDisabled: boolean;
  shouldShowGetNewBinButton: boolean;
  shouldShowMaxQtyWarningBanner: boolean;
  isProductTooLargeForAllBins: boolean;
  hasUserChangedBin: boolean;
  isFoodSafetyModalOpen: boolean;
  isSanitizeModalOpen: boolean;
  wholeBinPortOverrides: number[];
  /** Suppresses reopening the food safety modal (set when user confirms existing whole bins only) */
  hasSelectedAlreadyWholeBins: boolean;
  /** Food safety UI mode without port overrides (e.g. user dismissed the modal) */
  hasDeclinedPortOverride: boolean;
  prioritizedBinNumber: number | null;
  /** Only truthy when the previous bin flow is active */
  previousBinNumber: number | null;
  /** Is updated after every putaway. Used to populate the Product Card during previous bin flow */
  previousPutawayTask: PreviousPutawayTask | null;
  sanitizedBins: number[];
};

const initialState: AutostorePutawayState = {
  selectedRowId: null,
  selectedCompartment: undefined,
  newSelectedCompartment: null,
  changedQuantity: undefined,
  inventoryDate: dayjs().utc().add(1, "year").toDate(),
  lotNumber: "",
  isLotNumberFieldFocused: false,
  searchData: {
    scannedBarcodes: [],
    offset: 1
  },
  isPutawayTaskTableRefreshed: false,
  portPollingActive: false,
  closePortBtnDisabled: true,
  onLeaveInputTextShown: false,
  isConfirmInductionButtonDisabled: false,
  shouldShowGetNewBinButton: false,
  shouldShowMaxQtyWarningBanner: false,
  isProductTooLargeForAllBins: false,
  hasUserChangedBin: false,
  isFoodSafetyModalOpen: false,
  isSanitizeModalOpen: false,
  wholeBinPortOverrides: emptyArray,
  hasSelectedAlreadyWholeBins: false,
  hasDeclinedPortOverride: false,
  prioritizedBinNumber: null,
  previousBinNumber: null,
  previousPutawayTask: null,
  sanitizedBins: []
};

export const autostorePutawaySlice = createSlice({
  name: "autostorePutaway",
  initialState,
  reducers: {
    selectRow(state, { payload }: PayloadAction<Guid | null>) {
      state.selectedRowId = payload;
      state.hasUserChangedBin = false;
      state.prioritizedBinNumber = null;
    },

    // Compartment number 0-3
    selectCompartment(state, { payload }: PayloadAction<number | undefined>) {
      state.selectedCompartment = payload;
    },

    // Compartment number used for selecting a new compartment
    selectNewCompartment(state, { payload }: PayloadAction<number | null>) {
      state.newSelectedCompartment = payload;
    },

    setChangedQuantity(state, { payload }: PayloadAction<number | undefined>) {
      state.changedQuantity = payload;
    },

    setInventoryDate(state, { payload }: PayloadAction<Date | string | null>) {
      state.inventoryDate = payload;
    },

    setLotNumber(state, { payload }: PayloadAction<string>) {
      state.lotNumber = payload;
    },

    setIsLotNumberFieldFocused(state, { payload }: PayloadAction<boolean>) {
      state.isLotNumberFieldFocused = payload;
    },

    setSearchData(state, { payload }: PayloadAction<SearchData>) {
      state.searchData = payload;
    },

    setIsPutawayTaskTableRefreshed(state, { payload }: PayloadAction<boolean>) {
      state.isPutawayTaskTableRefreshed = payload;
    },

    setPortPollingActive(state, { payload }: PayloadAction<boolean>) {
      state.portPollingActive = payload;
    },

    setClosePortBtnDisabled(state, { payload }: PayloadAction<boolean>) {
      state.closePortBtnDisabled = payload;
    },

    setOnLeaveInputTextShown(state, { payload }: PayloadAction<boolean>) {
      state.onLeaveInputTextShown = payload;
    },

    setShouldShowGetNewBinButton(state, { payload }: PayloadAction<boolean>) {
      state.shouldShowGetNewBinButton = payload;
    },

    setShouldShowMaxQtyWarningBanner(
      state,
      { payload }: PayloadAction<boolean>
    ) {
      state.shouldShowMaxQtyWarningBanner = payload;
    },

    setIsProductTooLargeForAllBins(state, { payload }: PayloadAction<boolean>) {
      state.isProductTooLargeForAllBins = payload;
      if (payload) {
        state.shouldShowGetNewBinButton = false;
        state.shouldShowMaxQtyWarningBanner = false;
        state.selectedCompartment = undefined;
      }
    },

    setHasUserChangedBin(state, { payload }: PayloadAction<boolean>) {
      state.hasUserChangedBin = payload;
      if (payload) {
        state.isProductTooLargeForAllBins = false;
        state.prioritizedBinNumber = null;
      }
    },

    openFoodSafetyModal(state) {
      state.isFoodSafetyModalOpen = true;
    },
    closeFoodSafetyModal(state) {
      state.isFoodSafetyModalOpen = false;
      // Keep food safety UI mode so non-whole bins stay inactive; do not set
      // hasSelectedAlreadyWholeBins so the modal can reopen on a new row selection
      state.hasDeclinedPortOverride = true;
    },
    openSanitizeModal(state) {
      state.isSanitizeModalOpen = true;
    },
    closeSanitizeModal(state) {
      state.isSanitizeModalOpen = false;
    },
    setBinOverrides(state, { payload }: PayloadAction<number[]>) {
      state.isFoodSafetyModalOpen = false;

      if (payload.length) {
        // Update the state which will trigger the useStartInduction hook to run
        state.wholeBinPortOverrides = payload;
      } else {
        state.hasSelectedAlreadyWholeBins = true;
      }
    },
    clearBinOverrides(state) {
      state.wholeBinPortOverrides = emptyArray;
      state.selectedRowId = null;
      state.hasSelectedAlreadyWholeBins = false;
      state.hasDeclinedPortOverride = false;
    },

    setOptimalBin(
      state,
      {
        payload
      }: PayloadAction<GetSuggestedCompartmentAndMaxFillResponse | null>
    ) {
      if (!payload) {
        state.shouldShowGetNewBinButton = true;
        state.shouldShowMaxQtyWarningBanner = false;
        state.selectedCompartment = undefined;
        return;
      }

      if (
        !state.hasUserChangedBin &&
        state.changedQuantity &&
        payload?.maxFillQuantity &&
        state.changedQuantity > payload.maxFillQuantity
      ) {
        state.shouldShowMaxQtyWarningBanner = true;
      } else {
        state.shouldShowMaxQtyWarningBanner = false;
      }

      state.selectedCompartment = payload.suggestedCompartment - 1;
      state.shouldShowGetNewBinButton = false;
      state.isProductTooLargeForAllBins = false;
    },

    clearOptimalBin(state) {
      state.shouldShowGetNewBinButton = false;
      state.shouldShowMaxQtyWarningBanner = false;
      state.isProductTooLargeForAllBins = false;
      state.prioritizedBinNumber = null;
      state.selectedCompartment = undefined;
    },

    setPreviousBinNumber(state, { payload }: PayloadAction<number | null>) {
      state.previousBinNumber = payload;
      state.selectedRowId = null;
      state.changedQuantity = state.previousPutawayTask?.quantity.value;
    },

    setPreviousPutawayTask(
      state,
      { payload }: PayloadAction<PreviousPutawayTask>
    ) {
      state.previousPutawayTask = payload;
    },

    setPrioritizedBinNumber(state, { payload }: PayloadAction<number | null>) {
      state.prioritizedBinNumber = payload;
    },

    addSanitizedBin(state, { payload }: PayloadAction<number>) {
      state.sanitizedBins.push(payload);
    },

    removeSanitizedBin(state, { payload }: PayloadAction<number>) {
      state.sanitizedBins = state.sanitizedBins.filter(
        (bin) => bin !== payload
      );
    },

    clearSanitizedBins(state) {
      state.sanitizedBins = [];
    },

    reset(state) {
      state.selectedRowId = null;
      state.selectedCompartment = undefined;
      state.newSelectedCompartment = null;
      state.inventoryDate = new Date(Date.now() + 3600 * 1000 * 24 * 365); // +1 year
      state.prioritizedBinNumber = null;
    }
  },
  selectors: {
    selectSelectedCompartment: (state) => state.selectedCompartment,
    selectChangedQuantity: (state) => state.changedQuantity,
    selectLotNumber: (state) => state.lotNumber,
    selectIsLotNumberFieldFocused: (state) => state.isLotNumberFieldFocused,
    selectIsConfirmInductionButtonDisabled: (state) =>
      state.isConfirmInductionButtonDisabled,
    selectShouldShowGetNewBinButton: (state) =>
      state.shouldShowGetNewBinButton && !state.hasUserChangedBin,
    selectShouldShowMaxQtyWarningBanner: (state) =>
      state.shouldShowMaxQtyWarningBanner,
    selectIsProductTooLargeForAllBins: (state) =>
      state.isProductTooLargeForAllBins,
    selectHasUserChangedBin: (state) => state.hasUserChangedBin,
    selectPrioritizedBinNumber: (state) => state.prioritizedBinNumber,
    selectIsPreviousBinFlow: (state) => !!state.previousBinNumber,
    selectPreviousBinNumber: (state) => state.previousBinNumber,
    selectPreviousPutawayTask: (state) => state.previousPutawayTask
  },
  extraReducers: (builder) => {
    const { getSuggestedCompartmentsAndMaxFillCounts } = cubingApi.endpoints;
    builder.addMatcher(
      getSuggestedCompartmentsAndMaxFillCounts.matchRejected,
      (state) => {
        state.isConfirmInductionButtonDisabled = true;
      }
    );
    builder.addMatcher(
      getSuggestedCompartmentsAndMaxFillCounts.matchPending,
      (state) => {
        state.isConfirmInductionButtonDisabled = true;
      }
    );
    builder.addMatcher(
      getSuggestedCompartmentsAndMaxFillCounts.matchFulfilled,
      (state) => {
        state.isConfirmInductionButtonDisabled = false;
      }
    );
  }
});

export const {
  selectRow,
  selectCompartment,
  setChangedQuantity,
  setInventoryDate,
  setLotNumber,
  setIsLotNumberFieldFocused,
  setSearchData,
  setIsPutawayTaskTableRefreshed,
  setPortPollingActive,
  setClosePortBtnDisabled,
  setOnLeaveInputTextShown,
  selectNewCompartment,
  setShouldShowGetNewBinButton,
  setShouldShowMaxQtyWarningBanner,
  setIsProductTooLargeForAllBins,
  setHasUserChangedBin,
  setPrioritizedBinNumber,
  setOptimalBin,
  clearOptimalBin,
  openFoodSafetyModal,
  closeFoodSafetyModal,
  openSanitizeModal,
  closeSanitizeModal,
  clearBinOverrides,
  setBinOverrides,
  setPreviousBinNumber,
  setPreviousPutawayTask,
  addSanitizedBin,
  removeSanitizedBin,
  clearSanitizedBins,
  reset
} = autostorePutawaySlice.actions;

export const {
  selectSelectedCompartment,
  selectChangedQuantity,
  selectLotNumber,
  selectIsLotNumberFieldFocused,
  selectIsConfirmInductionButtonDisabled,
  selectShouldShowGetNewBinButton,
  selectShouldShowMaxQtyWarningBanner,
  selectIsProductTooLargeForAllBins,
  selectHasUserChangedBin,
  selectPrioritizedBinNumber,
  selectIsPreviousBinFlow,
  selectPreviousBinNumber,
  selectPreviousPutawayTask
} = autostorePutawaySlice.selectors;
